/** 新建/编辑项目身份；保存项目不隐式启动模型，章节可任意不连续选择。 */
import { LandingCopySchema, LandingProjectTagSchema } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  type ProjectDetail,
  type ProjectMutation,
  landingProjectQueryKey,
  useLandingMediaSources,
  useLandingProductsPage,
  useLandingProjectBook,
  useLandingProjectBookChapters,
  useLandingProjectDefaultCopy,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { Icons } from '~/lib/icons';
import { BookLanguageRowPicker } from '~/ui/BookLanguageRowPicker';
import { Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { SearchInput } from '~/ui/SearchInput';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';
import { toggleSelection } from './landing-project-display';
export function LandingProjectEditor({
  project,
  onClose,
  onSaved,
}: { project?: ProjectDetail; onClose: () => void; onSaved: (result: ProjectMutation) => void }) {
  const [name, setName] = useState(project?.name ?? '');
  const [note, setNote] = useState(project?.note ?? '');
  const [tag, setTag] = useState(project?.tag ?? '');
  const trimmedTag = tag.trim();
  const tagError = trimmedTag
    ? (LandingProjectTagSchema.safeParse(trimmedTag).error?.issues[0]?.message ?? null)
    : null;
  const [novelId, setNovelId] = useState(project?.novel.id ?? '');
  const [productId, setProductId] = useState(project?.product.id ?? '');
  const [channel, setChannel] = useState(project?.media_source ?? '');
  const [search, setSearch] = useState('');
  const initializedBook = useRef<string | null>(null);
  const [paidExpanded, setPaidExpanded] = useState(false);
  const [selected, setSelected] = useState(new Set<string>(project?.chapter_ids ?? []));
  const products = useLandingProductsPage({ page: 1, pageSize: 200 });
  const channels = useLandingMediaSources();
  const book = useLandingProjectBook(novelId);
  const defaultCopy = useLandingProjectDefaultCopy(
    project ? '' : productId,
    book.data?.id === novelId ? book.data.locale : '',
  );
  const copyReady =
    defaultCopy.isSuccess && LandingCopySchema.safeParse(defaultCopy.data.copy).success;
  const copyError = defaultCopy.isError
    ? defaultCopy.error.message
    : defaultCopy.isSuccess && !copyReady
      ? `${book.data?.locale ?? ''} 默认文案不完整，请在投放设置中补齐对应产品的该语言文案`
      : null;
  const chapters = useLandingProjectBookChapters(project ? '' : novelId);
  useEffect(() => {
    if (project || !novelId || !chapters.data || initializedBook.current === novelId) return;
    initializedBook.current = novelId;
    setSelected(
      new Set(
        chapters.data
          .filter((row) => row.is_free && row.published_at && row.snapshot_version > 0)
          .map((row) => row.id),
      ),
    );
  }, [project, novelId, chapters.data]);
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: () =>
      project
        ? landingProjectsApi.update(project.id, {
            name: name.trim(),
            note,
            tag: trimmedTag || null,
          })
        : landingProjectsApi.create({
            name: name.trim() || `${book.data?.cn_title ?? '新项目'} · ${channel}`,
            note,
            tag: trimmedTag || null,
            novel_id: novelId,
            product_id: productId,
            media_source: channel,
            chapter_ids: [...selected],
          }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      onSaved(result);
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const rows = (chapters.data ?? []).filter((row) =>
    `${row.chapter_index} ${row.title}`.toLowerCase().includes(search.toLowerCase()),
  );
  const freeRows = rows.filter((row) => row.is_free);
  const paidRows = rows.filter((row) => !row.is_free);
  const renderRows = (items: typeof rows, group: '免费' | '付费') => (
    <>
      <button
        type="button"
        className="btn btn-sm"
        style={{ marginTop: 8 }}
        onClick={() =>
          setSelected(
            (current) =>
              new Set([
                ...current,
                ...items
                  .filter((row) => row.published_at && row.snapshot_version > 0)
                  .map((row) => row.id),
              ]),
          )
        }
      >
        全选当前筛选的可用{group}章节
      </button>
      <div style={{ maxHeight: 280, overflow: 'auto', marginTop: 8 }}>
        {items.map((row) => (
          <label
            key={row.id}
            className="hstack landing-chapter-choice"
            style={{ gap: 8, padding: 7 }}
          >
            <input
              type="checkbox"
              aria-label={`选择第 ${row.chapter_index} 章`}
              checked={selected.has(row.id)}
              disabled={!row.published_at || row.snapshot_version < 1}
              onChange={() => setSelected((current) => toggleSelection(current, row.id))}
            />
            <span className="mono">{row.chapter_index}</span>
            <span className="landing-chapter-choice-title">{row.title}</span>
            <span className={`chip ${row.is_free ? 'chip-pos' : 'chip-warn'}`}>
              {row.is_free ? '免费' : '付费'}
            </span>
            {!row.published_at && <span className="muted">未发布，不可选择</span>}
          </label>
        ))}
        {!items.length && <p className="muted">没有匹配的{group}章节</p>}
      </div>
    </>
  );
  return (
    <Dialog
      title={project ? '编辑项目信息' : '新建投放项目'}
      subtitle="项目引用一本具体语言版书；保存不调用模型"
      icon="IconLayers"
      width={860}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={
              save.isPending ||
              !!tagError ||
              (!project &&
                (!novelId ||
                  !productId ||
                  !channel ||
                  !selected.size ||
                  !copyReady ||
                  defaultCopy.isFetching)) ||
              (project && !name.trim())
            }
            onClick={() => save.mutate()}
          >
            {save.isPending ? '保存中…' : '保存项目'}
          </button>
        </>
      }
    >
      <div style={{ padding: '0 18px' }}>
        <FormRow label="项目名称" hint="留空时根据书籍与渠道建议名称">
          <input
            className="input"
            aria-label="项目名称"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </FormRow>
        <FormRow label="备注">
          <textarea
            className="input"
            aria-label="项目备注"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </FormRow>
        <FormRow label="TAG" hint="可选，拼入 AppLovin Creative-set 名称的 TAG 段，如 test01">
          <input
            className="input mono"
            aria-label="项目 TAG"
            maxLength={32}
            value={tag}
            onChange={(event) => setTag(event.target.value)}
          />
          {tagError && (
            <p role="alert" className="muted" style={{ color: 'var(--neg)' }}>
              {tagError}
            </p>
          )}
        </FormRow>
        {!project && (
          <>
            <FormRow label="投放产品" required>
              <Select
                value={productId}
                onChange={(event) => {
                  if (event.target.value === productId) return;
                  setProductId(event.target.value);
                  initializedBook.current = null;
                  setNovelId('');
                  setSelected(new Set());
                  setSearch('');
                  setPaidExpanded(false);
                }}
              >
                <option value="">选择产品…</option>
                {products.data.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name} · {row.brand}
                  </option>
                ))}
              </Select>
              {products.isError && <p role="alert">投放产品加载失败</p>}
            </FormRow>
            <FormRow label="书籍语言版" required>
              <BookLanguageRowPicker
                value={novelId}
                landingProductId={productId || undefined}
                disabled={!productId}
                book={book.data}
                bookLookupFailed={book.isError}
                onChange={(id) => {
                  if (id === novelId) return;
                  initializedBook.current = null;
                  setNovelId(id);
                  setSelected(new Set());
                  setSearch('');
                  setPaidExpanded(false);
                }}
              />
              {novelId &&
                productId &&
                (copyError ? (
                  <div role="alert" className="card-flat" style={{ padding: 12, marginTop: 8 }}>
                    <p>{copyError}</p>
                    <a
                      className="btn btn-sm"
                      href="/landing/settings"
                      target="_blank"
                      rel="noreferrer"
                    >
                      打开投放设置
                    </a>{' '}
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={defaultCopy.isFetching}
                      onClick={() => void defaultCopy.refetch()}
                    >
                      重新检查文案
                    </button>
                  </div>
                ) : defaultCopy.isPending || defaultCopy.isFetching ? (
                  <p role="status" className="muted">
                    正在检查对应语言的默认文案…
                  </p>
                ) : null)}
            </FormRow>
            <FormRow label="投放渠道" required>
              <Select value={channel} onChange={(event) => setChannel(event.target.value)}>
                <option value="">选择渠道…</option>
                {channels.data.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </Select>
            </FormRow>
            <FormRow
              label="选择章节"
              required
              hint={`已选 ${selected.size} 章，可选择任意不连续章节`}
            >
              <SearchInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="搜索章号 / 标题"
              />
              <p className="muted">
                首次选择书籍时默认勾选全部可用已发布免费章；可手动调整任意章节。
              </p>
              {chapters.isError ? (
                <div role="alert" className="card-flat" style={{ padding: 12 }}>
                  <p>{chapters.error.message}</p>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => void chapters.refetch()}
                  >
                    重新加载
                  </button>
                </div>
              ) : chapters.isPending && novelId ? (
                <p>加载章节…</p>
              ) : (
                <>
                  {renderRows(freeRows, '免费')}
                  <details
                    className="card-flat landing-chapter-collapse"
                    style={{ marginTop: 12 }}
                    open={paidExpanded}
                    onToggle={(event) => setPaidExpanded(event.currentTarget.open)}
                  >
                    <summary>
                      <Icons.IconChevRight size={12} className="landing-collapse-chevron" />
                      付费章节（{paidRows.length} 章，已选{' '}
                      {paidRows.filter((row) => selected.has(row.id)).length}）
                    </summary>
                    {paidExpanded && (
                      <div className="landing-collapse-content">{renderRows(paidRows, '付费')}</div>
                    )}
                  </details>
                </>
              )}
            </FormRow>
          </>
        )}
      </div>
    </Dialog>
  );
}
